import { useClipboard } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'ui-components';
import {
  IManagerApiModuleManagerBenchmark,
  IManagerApiModuleManagerPlatformInsight,
} from '~/clientApi/managers.model';
import { FEATURE_FLAGS, SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { getDetailEmailFromCreator } from '~/core/utils/creator/detailedEmailTemplate';
import { getBasicEmailFromCreator } from '~/core/utils/creator/emailTemplate';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useGetCreatorInsightsAndBenchmarks } from '~/screens/WhalarModeTalentRecord/hooks/useGetCreatorInsightsAndBenchmarks';
import { ICreator } from '~/types';

export const useCreatorCopyEmbed = (creator: ICreator) => {
  const snackbar = useSnackbar();
  const [benchmarksAndInsightsId, setBenchmarksAndInsightsId] =
    useState<ICreator | null>(null);

  const [copyStatus, setCopyStatus] = useState('idle');
  const isFoamEnableAlignmentFFEnabled =
    useFeatureFlag(FEATURE_FLAGS.foamEmbedsAlignment) === 'enabled';
  const isFoamTalentLocationFFEnabled =
    useFeatureFlag(FEATURE_FLAGS.foamTalentLocation) === 'enabled';

  const { onCopy: onCopyEmbed, setValue } = useClipboard('', {
    format: 'text/html',
  });

  const { benchmarks, insights, isFetching, isError } =
    useGetCreatorInsightsAndBenchmarks(benchmarksAndInsightsId);

  const handleBasicClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const textToCopy = getBasicEmailFromCreator(
        creator,
        isFoamEnableAlignmentFFEnabled,
        isFoamTalentLocationFFEnabled,
      );

      setValue(textToCopy);
      setCopyStatus('copying');
    },
    [
      creator,
      setValue,
      setCopyStatus,
      isFoamEnableAlignmentFFEnabled,
      isFoamTalentLocationFFEnabled,
    ],
  );

  const handleDetailClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setCopyStatus('idle');
      setBenchmarksAndInsightsId(creator);
    },
    [creator, setCopyStatus],
  );

  useEffect(() => {
    if (!isError) return;

    setBenchmarksAndInsightsId(null);
    snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
  }, [isError, snackbar, setBenchmarksAndInsightsId]);

  useEffect(() => {
    if (!benchmarks || !insights || copyStatus !== 'idle') return;

    const textToCopy = getDetailEmailFromCreator(
      creator,
      isFoamEnableAlignmentFFEnabled,
      isFoamTalentLocationFFEnabled,
      [benchmarks as IManagerApiModuleManagerBenchmark],
      [insights as IManagerApiModuleManagerPlatformInsight],
    );
    setValue(textToCopy);
    setCopyStatus('copying');
  }, [
    creator,
    benchmarks,
    insights,
    copyStatus,
    isError,
    snackbar,
    isFoamEnableAlignmentFFEnabled,
    isFoamTalentLocationFFEnabled,
    setValue,
    setCopyStatus,
    setBenchmarksAndInsightsId,
  ]);

  useEffect(() => {
    if (copyStatus === 'copying') {
      onCopyEmbed();
      setCopyStatus('done');

      snackbar.success({
        isCloseable: false,
        description: 'Copied!',
        duration: 2000,
      });
    }
  }, [copyStatus, snackbar, setCopyStatus, onCopyEmbed]);

  return {
    onDetailClick: handleDetailClick,
    onBasicClick: handleBasicClick,
    isDetailLoading: isFetching,
    copyStatus,
  };
};
